import type { Order, OrderItem } from '../../../../../types/apiResponse/order';
import { roundOrderMoney } from '../../orderMoney';
import { number } from './presentation';

const firstNumber = (...values: unknown[]) =>
  values.map(number).find((value) => value !== null) ?? null;
const percent = (value: unknown) => Math.min(100, Math.max(0, number(value) ?? 0));

// Espelha calculateOrderItemPriceSummary e a leitura de pedidos sincronizados do Sales.
// ValorVenda não é descontado novamente: a base preferencial é sempre Preco.
export function itemFinancials(item: OrderItem, decimals: number) {
  const round = (value: number) => roundOrderMoney(value, decimals);
  const rawQuantity = number(item.QtdVenda);
  const quantity = rawQuantity === null ? null : Math.max(0, rawQuantity);
  const rowGross = number(item.ValorBruto);
  const grossUnit = quantity && rowGross !== null ? rowGross / quantity : null;
  const price = firstNumber(item.Preco, item.ValorVenda, item.PrecoTabelaBruto, grossUnit);
  const table = firstNumber(
    item.preco_tabela,
    item.PrecoTabelaBruto,
    item.PrecoRegiao,
    grossUnit,
    price,
  );
  const discountPercent = percent(item.Desconto1);
  const basePrice = price === null ? null : round(Math.max(0, price));
  const tablePrice = table === null ? null : round(Math.max(0, table));
  const netPrice = basePrice === null ? null : round(basePrice * (1 - discountPercent / 100));
  const netTotal =
    netPrice === null || quantity === null ? null : round(netPrice * Math.max(0, quantity));
  const grossTotal =
    tablePrice === null || quantity === null ? null : round(tablePrice * Math.max(0, quantity));
  const adjustment =
    netTotal === null || grossTotal === null
      ? null
      : grossTotal <= 0 || quantity === 0
        ? 0
        : round(netTotal - grossTotal);
  const discount = adjustment === null ? null : Math.max(0, -adjustment);
  const surcharge = adjustment === null ? null : Math.max(0, adjustment);
  const adjustmentPercent =
    adjustment === null
      ? null
      : grossTotal && grossTotal > 0
        ? roundOrderMoney((Math.abs(adjustment) / grossTotal) * 100, 2)
        : 0;
  return {
    quantity,
    basePrice,
    tablePrice,
    netPrice,
    netTotal,
    grossTotal,
    discountPercent,
    discount,
    surcharge,
    adjustmentPercent,
  };
}

export function orderFinancials(order: Order, decimals: number) {
  const round = (value: number) => roundOrderMoney(value, decimals);
  const items = order.items?.map((item) => itemFinancials(item, decimals));
  const sum = (key: 'netTotal' | 'grossTotal' | 'discount' | 'surcharge' | 'quantity') => {
    if (!items || items.some((item) => item[key] === null)) return null;
    return items.reduce(
      (total, item) => (key === 'quantity' ? total + item[key]! : round(total + item[key]!)),
      0,
    );
  };
  const itemsNet = sum('netTotal');
  const generalPercent = percent(order.Desconto1);
  const generalDiscount = itemsNet === null ? null : round((itemsNet * generalPercent) / 100);
  const calculatedNet =
    itemsNet === null || generalDiscount === null ? null : round(itemsNet - generalDiscount);
  const itemDiscount = sum('discount');
  const totalDiscount =
    itemDiscount === null || generalDiscount === null
      ? null
      : round(itemDiscount + generalDiscount);
  // Como no modo visualizar do Sales, bruto/líquido sincronizados prevalecem.
  const gross = number(order.ValorBruto) ?? sum('grossTotal');
  const net = number(order.ValorLiquido) ?? calculatedNet;
  const percentOfGross = (value: number | null) =>
    value === null ? null : gross && gross > 0 ? (value / gross) * 100 : 0;
  return {
    gross,
    net,
    itemsNet,
    calculatedNet,
    itemDiscount,
    generalDiscount,
    generalPercent,
    totalDiscount,
    itemPercent: percentOfGross(itemDiscount),
    totalPercent: percentOfGross(totalDiscount),
    surcharge: sum('surcharge'),
    quantity: sum('quantity'),
    count: items?.length ?? number(order.qtd_itens),
  };
}
